import { EmailTextInput, FloatingFormItem, Group } from '@/components/_common';
import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';
import { MailSendingForm } from '@/core/domain/mail/MailControler/MailControler.types';
import { ContactType } from '@/core/domain/_shared/ContactType';
import { useDefaultEmptyFieldError } from '@/hooks';
import flatContacts from '@/utils/functions/flatContacts';
import {
  AutoComplete,
  Button,
  Card,
  Checkbox,
  Form,
  FormInstance,
  Input,
  Progress,
} from 'antd';
import { Download } from 'react-feather';
import { v4 } from 'uuid';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import { useQuestReportWorkOrderAdministrativeReport } from '@/core/domain/questReport/WorkOrder/WorkOder.hooks';
import { useEffect } from 'react';
import { AiOutlineFile } from 'react-icons/ai';
import { useCustomerContacts } from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';

type Props = {
  form: FormInstance<MailSendingForm>;
  isEdit?: boolean;
};

const MailSendForm: React.FC<Props> = ({ isEdit, form }) => {
  const requiredRole = useDefaultEmptyFieldError();
  const sendWorkorder = Form.useWatch<boolean>('sendWorkorder', form);
  const attachment = Form.useWatch<boolean>('attachment', form);

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const { isLoading, mutateAsync: getReport } =
    useQuestReportWorkOrderAdministrativeReport();

  useEffect(() => {
    if (sendWorkorder && workorder && complement) {
      (async () => {
        const result = await getReport({
          complementNumber: complement.number,
          workOrderId: workorder.id,
          includeDeletedParts: false,
        });
        form.setFieldValue(
          'attachment',
          Buffer.from(result as ArrayBuffer).toString('base64'),
        );
      })();
    }
  }, [sendWorkorder]);

  const customerContacts = useCustomerContacts(workorder?.customer?.id);

  const contacts = flatContacts(customerContacts.data).filter(
    (contact) => contact.contactType === ContactType.Email,
  );

  function downloadPDF() {
    const linkSource = `data:application/pdf;base64,${attachment}`;
    const downloadLink = document.createElement('a');
    const fileName = `${v4()}.pdf`;
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  }

  return (
    <Form
      form={form}
      onValuesChange={async (field: MailSendingForm) => {
        if (field.sendWorkorder === false) {
          form.setFieldValue('attachment', null);
        }
      }}
    >
      <Form.Item hidden name="fromName" noStyle>
        <Input disabled={isEdit} />
      </Form.Item>
      <Form.Item hidden name="attachment" noStyle>
        <Input />
      </Form.Item>

      <FloatingFormItem label="Para" name="to" rules={[requiredRole]}>
        <AutoComplete
          options={contacts.map((contact) => ({ value: contact.value }))}
          allowClear
          disabled={isEdit}
        />
      </FloatingFormItem>

      <FloatingFormItem label="Assunto" name="subject">
        <Input disabled={isEdit} />
      </FloatingFormItem>

      <Form.Item name="message">
        <EmailTextInput style={{ height: 300 }} readyOnly={isEdit} />
      </Form.Item>

      {!isEdit && (
        <>
          <Form.Item name="sendWorkorder" valuePropName="checked">
            <Checkbox>Enviar orçamento em anexo (PDF).</Checkbox>
          </Form.Item>

          {isLoading && (
            <Card bordered>
              <div>Importando o PDF do orçamento</div>
              <Progress percent={isLoading ? 25 : 50} showInfo={false} />
            </Card>
          )}

          {!isLoading && !!attachment && (
            <Card bordered>
              <Group gap={16}>
                <AiOutlineFile size={24} />
                <span>PDF do orçamento anexado</span>
              </Group>
            </Card>
          )}
        </>
      )}

      {isEdit && !!attachment && (
        <Button
          icon={<AntdFeatherIcon icon={<Download />} size={15} />}
          onClick={downloadPDF}
        >
          Baixar PDF do orçamento em anexo
        </Button>
      )}
    </Form>
  );
};

export default MailSendForm;
